import { z } from "zod";
import { fieldSchema } from "./fields.js";
/**
 * NOTE ON THE TWO "VERSIONS" IN THIS SYSTEM — do not conflate them:
 *
 *   formatVersion          the shape of this document. Bumped when koaform
 *                          itself gains a capability that changes the schema.
 *   form_versions.version  the publish counter for one form. Bumped every time
 *                          a user publishes.
 */
export const FORMAT_VERSION = 1;
/** Rejects `javascript:` and friends. Redirect targets must be http(s). */
export const httpUrlSchema = z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), {
    message: "Must be an http(s) URL.",
});
const uniqueIds = (fields, ctx) => {
    const seenFields = new Set();
    fields.forEach((field, i) => {
        if (seenFields.has(field.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Duplicate field id.",
                path: [i, "id"],
            });
        }
        seenFields.add(field.id);
        if ("choices" in field) {
            const seenChoices = new Set();
            field.choices.forEach((choice, j) => {
                if (seenChoices.has(choice.id)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Duplicate choice id.",
                        path: [i, "choices", j, "id"],
                    });
                }
                seenChoices.add(choice.id);
            });
        }
    });
};
/**
 * Per-form look. Colors are strict 6-digit hex — these values end up inside
 * inline styles on the public page, so the grammar is deliberately too small
 * to smuggle anything through. The banner is http(s) only for the same reason.
 */
const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex color like #1d6be5."); // prettier-ignore
/**
 * An uploaded theme image lives on the API as an app-relative path, so a
 * published schema keeps working when the API moves hosts. Only files the
 * theme-image endpoint can actually have written are representable.
 */
const themeImagePathSchema = z
    .string()
    .regex(/^\/i\/theme\/[A-Za-z0-9_-]+\/(logo|banner)\.(png|jpg|webp)(\?v=\d+)?$/); // prettier-ignore
const themeImageSchema = z.union([httpUrlSchema, themeImagePathSchema]);
/**
 * Typefaces a form may use. Every one resolves from fonts already present on
 * the device (or the brand font we self-host) — a hosted form must never
 * fetch a webfont from a third party, for both speed and respondent privacy.
 */
export const FORM_FONTS = [
    "default",
    "system",
    "serif",
    "mono",
    "rounded",
    "humanist",
    "slab",
    "condensed",
];
export const formThemeSchema = z.object({
    /** Buttons, selected choices, focus rings. Absent = brand blue. */
    accent: hexColorSchema.optional(),
    /** The page behind the form card. Absent = paper. */
    background: hexColorSchema.optional(),
    /** Banner image above the form: uploaded, or a pasted http(s) URL. */
    bannerUrl: themeImageSchema.optional(),
    /** Small square logo at the top of the form card. */
    logoUrl: themeImageSchema.optional(),
    /** Typeface family. Only self-hosted stacks — a form never loads remote fonts. */
    font: z.enum(FORM_FONTS).optional(),
});
export const confirmationSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("message"),
        message: z.string().max(2000),
    }),
    z.object({
        type: z.literal("redirect"),
        url: httpUrlSchema,
    }),
]);
export const formSettingsSchema = z.object({
    submitText: z.string().trim().max(60).default("Submit"),
    confirmation: confirmationSchema.default({
        type: "message",
        message: "Thanks for your submission!",
    }),
    closed: z.boolean().default(false),
    closedMessage: z.string().max(500).optional(),
    /** Turnstile. Off is a support-gated setting, not a self-serve toggle. */
    captcha: z.boolean().default(true),
    /** Master switch. False means this form never emails its owner. */
    notifyOwner: z.boolean().default(true),
    /**
     * How the owner hears about responses.
     *
     * Kept separate from notifyOwner rather than folded into one three-way enum
     * so that every document already stored — none of which has this key —
     * keeps its current behaviour without a data migration.
     *
     * "each" still falls back to a digest on a form that suddenly gets busy;
     * that is a safety valve on our sending reputation, not a preference, so it
     * is not something a form can turn off.
     */
    notifyMode: z.enum(["each", "digest"]).default("each"),
    /** Stop accepting responses after this many. Null = unlimited. */
    responseLimit: z.number().int().min(1).nullable().default(null),
    /** ISO datetime after which the form closes itself. */
    closesAt: z.string().nullable().default(null),
    theme: formThemeSchema.prefault({}),
});
export const formDocSchema = z.object({
    formatVersion: z.literal(FORMAT_VERSION),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    /**
     * Deliberately no `.min(1)`. An empty draft is legal — autosave of a brand
     * new form must not 400. It is `assertPublishable()` that requires a field.
     */
    fields: z.array(fieldSchema).max(200).superRefine(uniqueIds),
    // zod 4: .default() expects the parsed OUTPUT; .prefault() fills the
    // INPUT before parsing, which is what we want — an absent settings
    // object should get every field's own default.
    settings: formSettingsSchema.prefault({}),
});
export const emptyFormDoc = (title = "Untitled form") => formDocSchema.parse({
    formatVersion: FORMAT_VERSION,
    title,
    fields: [],
    settings: {},
});
//# sourceMappingURL=document.js.map